<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>

    <template #default>
      <div class="space-y-6">
        <Breadcrumbs :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Hero Halaman', href: route('pagehero.index') },
        ]" />

        <!-- Header card -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <h2 class="flex items-center gap-2 text-lg font-bold text-white">
              <i class="fa-solid fa-image"></i>
              Hero Halaman
            </h2>
            <p class="mt-1 text-sm text-indigo-100">
              Atur gambar, warna, judul, dan subjudul banner tiap halaman frontend.
            </p>
          </div>

          <div class="p-6 space-y-6">
            <div v-for="h in items" :key="h.page"
              class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
              <div class="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h4 class="text-sm font-bold tracking-wide text-gray-800 uppercase">{{ h.label }}</h4>
              </div>

              <div class="grid grid-cols-1 gap-6 p-5 lg:grid-cols-2">
                <!-- Form -->
                <div class="space-y-4">
                  <div>
                    <InputLabel value="Judul" />
                    <input v-model="h.title" type="text" placeholder="Judul halaman"
                      class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                  </div>
                  <div>
                    <InputLabel value="Subjudul" />
                    <textarea v-model="h.subtitle" rows="2" placeholder="Subjudul / deskripsi singkat"
                      class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"></textarea>
                  </div>
                  <div>
                    <InputLabel value="Warna Overlay" />
                    <div class="flex flex-wrap items-center gap-2 mt-1">
                      <input v-model="h.color" type="color"
                        class="w-10 h-10 p-0 bg-white border border-gray-300 rounded cursor-pointer" />
                      <input v-model="h.color" type="text" placeholder="#7a2f2f"
                        class="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                      <button v-for="c in presets" :key="c" type="button" @click="h.color = c"
                        class="w-7 h-7 border rounded-full shadow-sm" :style="{ backgroundColor: c }" :title="c"></button>
                    </div>
                  </div>
                  <div>
                    <InputLabel value="Gambar Latar" />
                    <input type="file" accept="image/*" @change="onFile(h, $event)"
                      class="block w-full mt-1 text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                    <p class="mt-1 text-xs text-gray-400">Kosongkan untuk mempertahankan gambar saat ini. Maks 2MB.</p>
                  </div>
                  <div>
                    <PrimaryBtn :disabled="h.saving" @click="save(h)"
                      class="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                      <i class="fa-solid fa-floppy-disk"></i>
                      {{ h.saving ? "Menyimpan..." : "Simpan" }}
                    </PrimaryBtn>
                  </div>
                </div>

                <!-- Preview -->
                <div>
                  <span class="block mb-1 text-xs font-medium text-gray-500">Pratinjau</span>
                  <div class="relative h-40 overflow-hidden rounded-xl">
                    <img :src="h.previewImage || defaultImg" class="object-cover w-full h-full" alt="" />
                    <div class="absolute inset-0" :style="overlay(h.color)"></div>
                    <div class="absolute bottom-0 left-0 p-4">
                      <p class="text-[10px] font-semibold tracking-widest uppercase text-white/70">Beranda / {{ h.label }}</p>
                      <h1 class="text-xl font-bold text-white font-playfair">{{ h.title || h.label }}</h1>
                      <p v-if="h.subtitle" class="mt-1 text-xs text-white/90 line-clamp-1">{{ h.subtitle }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { reactive, inject } from "vue";
import { router } from "@inertiajs/vue3";
import Swal from "sweetalert2";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";

const route = inject("route");

const props = defineProps({
  heroes: { type: Array, default: () => [] },
});

const defaultImg = "/storage/images/BGUNG.png";
const presets = ["#7a2f2f", "#1e3a8a", "#065f46", "#5b21b6", "#0f766e", "#9a3412", "#111827"];

const items = reactive(
  props.heroes.map((h) => ({
    page: h.page,
    label: h.label,
    title: h.title || "",
    subtitle: h.subtitle || "",
    color: h.color || "#7a2f2f",
    file: null,
    previewImage: h.image || null,
    saving: false,
  }))
);

const overlay = (color) => ({
  backgroundImage: `linear-gradient(to right, ${color}f2, ${color}d9, ${color}8c)`,
});

const onFile = (h, e) => {
  const f = e.target.files?.[0];
  if (!f) return;
  h.file = f;
  h.previewImage = URL.createObjectURL(f);
};

const save = (h) => {
  h.saving = true;
  const fd = new FormData();
  fd.append("title", h.title || "");
  fd.append("subtitle", h.subtitle || "");
  fd.append("color", h.color || "");
  if (h.file instanceof File) fd.append("image", h.file);

  router.post(route("pagehero.update", h.page), fd, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => {
      h.file = null;
      Swal.fire({ title: "Berhasil!", text: `Hero ${h.label} disimpan.`, icon: "success", timer: 1500, showConfirmButton: false });
    },
    onError: () => {
      Swal.fire({ title: "Gagal!", text: "Periksa kembali input (gambar maks 2MB).", icon: "error" });
    },
    onFinish: () => { h.saving = false; },
  });
};
</script>
